import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DEFAULT_ANSWER_ARROW_COLOR,
  getMissAnimationDuration,
  useMissSequence,
  type ChessboardArrow,
} from 'react-chess-core';
import {
  replayRefutationEngineOptions,
  REPLAY_START_FEN,
} from 'react-chess-replay-trainer';
import { boardOrientationForTrainSide, turnFromFen } from '../../courses/courseLineUtils';
import { puzzleEngineOptions } from '../../common/puzzleEngineConfig';
import type { AnalyzedMovePly, MistakePly } from '../my-games/myGamesApi';
import { useGameLineBrowser } from '../my-games/useGameLineBrowser';
import { buildMoveQualitySquareStyles } from './buildMoveQualitySquareStyles';
import { shouldSuppressMoveQualityDot } from './shouldSuppressMoveQualityDot';
import { useMoveQualityBadgePly } from './useMoveQualityBadgePly';
import {
  REVIEW_MOVE_ANIMATION_MS,
  REVIEW_PREVIEW_LOOP_PAUSE_MS,
  watchAutoplayMissAnimationDuration,
  watchAutoplayMistakeAnswerMs,
  watchAutoplayPreRefutationBadgeMs,
  watchAutoplayRefutationMs,
  watchAutoplayWrongHoldMs,
} from './gameReviewAutoplayConstants';

export type GameReviewWatchAutoplayOptions = {
  gameId: string;
  movesUci: string[];
  mistakes: readonly MistakePly[];
  analyzedMoves: readonly AnalyzedMovePly[];
  userColor: 'w' | 'b' | null;
  autoplayStepMs: number;
  /** When false, autoplay never starts (e.g. static preview). */
  autoplayEnabled?: boolean;
  /** Restart from `startPlyIndex` after the last move. */
  loop?: boolean;
  startFen?: string;
  startPlyIndex?: number;
  startDelayMs?: number;
};

export function useGameReviewWatchAutoplay({
  gameId,
  movesUci,
  mistakes,
  analyzedMoves,
  userColor,
  autoplayStepMs,
  autoplayEnabled = true,
  loop = false,
  startFen = REPLAY_START_FEN,
  startPlyIndex = 0,
  startDelayMs = 0,
}: GameReviewWatchAutoplayOptions) {
  const browse = useGameLineBrowser(movesUci, gameId, startFen);
  const [watchAutoplay, setWatchAutoplay] = useState(autoplayEnabled);
  const [activeWatchMistake, setActiveWatchMistake] = useState<MistakePly | null>(
    null,
  );
  const [watchMissFeedback, setWatchMissFeedback] = useState<
    'incorrect' | null
  >(null);
  const [started, setStarted] = useState(false);
  const [pendingWatchMistakePause, setPendingWatchMistakePause] = useState(false);
  const pendingMistakeTimerRef = useRef<number | null>(null);

  useEffect(() => {
    setWatchAutoplay(autoplayEnabled);
    setActiveWatchMistake(null);
    setWatchMissFeedback(null);
    setStarted(false);
    setPendingWatchMistakePause(false);
    if (pendingMistakeTimerRef.current != null) {
      window.clearTimeout(pendingMistakeTimerRef.current);
      pendingMistakeTimerRef.current = null;
    }
  }, [autoplayEnabled, gameId, startFen, startPlyIndex]);

  useEffect(() => {
    browse.goTo(startPlyIndex);
  }, [browse.goTo, gameId, startFen, startPlyIndex]);

  const watchMistakeByPly = useMemo(() => {
    const map = new Map<number, MistakePly>();
    for (const mistake of mistakes) {
      if (userColor && turnFromFen(mistake.fen) !== userColor) {
        continue;
      }
      map.set(mistake.plyIndex, mistake);
    }
    return map;
  }, [mistakes, userColor]);

  const refutationEngine = useMemo(
    () => ({
      ...puzzleEngineOptions,
      ...replayRefutationEngineOptions,
    }),
    [],
  );

  const watchKnownRefutation = useMemo(() => {
    if (!activeWatchMistake?.refutationUci) {
      return null;
    }
    return {
      uci: activeWatchMistake.refutationUci,
      san: activeWatchMistake.refutationSan ?? null,
    };
  }, [activeWatchMistake]);

  const watchMissSequenceOptions = {
    refutationPauseMs: watchAutoplayRefutationMs(autoplayStepMs),
    wrongHoldMs: watchAutoplayWrongHoldMs(autoplayStepMs),
  };
  const watchMissSequence = useMissSequence(
    watchMissFeedback,
    activeWatchMistake?.bestUci ?? null,
    refutationEngine,
    DEFAULT_ANSWER_ARROW_COLOR,
    true,
    false,
    watchKnownRefutation,
    undefined,
    watchMissSequenceOptions,
  );

  const clearWatchMistake = useCallback(() => {
    setActiveWatchMistake(null);
    setWatchMissFeedback(null);
    watchMissSequence.clearSequence();
  }, [watchMissSequence.clearSequence]);

  const triggerWatchMistakePause = useCallback(
    (mistake: MistakePly) => {
      setActiveWatchMistake(mistake);
      setWatchMissFeedback('incorrect');
      watchMissSequence.startSequence(mistake.fen, mistake.playedUci);
    },
    [watchMissSequence],
  );

  useEffect(() => {
    if (!watchAutoplay || activeWatchMistake || pendingWatchMistakePause) {
      return undefined;
    }

    if (!browse.canNext) {
      if (!loop) {
        setWatchAutoplay(false);
        return undefined;
      }

      const id = window.setTimeout(() => {
        browse.goTo(startPlyIndex);
      }, REVIEW_PREVIEW_LOOP_PAUSE_MS);
      return () => window.clearTimeout(id);
    }

    const delay = !started ? startDelayMs + autoplayStepMs : autoplayStepMs;
    const id = window.setTimeout(() => {
      if (!started) {
        setStarted(true);
      }
      const mistake = watchMistakeByPly.get(browse.plyIndex);
      browse.goNext();
      if (mistake) {
        setPendingWatchMistakePause(true);
        pendingMistakeTimerRef.current = window.setTimeout(() => {
          pendingMistakeTimerRef.current = null;
          setPendingWatchMistakePause(false);
          triggerWatchMistakePause(mistake);
        }, REVIEW_MOVE_ANIMATION_MS + watchAutoplayPreRefutationBadgeMs(autoplayStepMs));
      }
    }, delay);

    return () => {
      window.clearTimeout(id);
      // Keep pendingMistakeTimerRef alive — this effect re-runs when pending starts.
    };
  }, [
    activeWatchMistake,
    pendingWatchMistakePause,
    autoplayStepMs,
    browse.canNext,
    browse.goNext,
    browse.goTo,
    browse.plyIndex,
    loop,
    startDelayMs,
    startPlyIndex,
    started,
    triggerWatchMistakePause,
    watchAutoplay,
    watchMistakeByPly,
  ]);

  useEffect(() => {
    if (!activeWatchMistake || watchMissSequence.display.animating) {
      return undefined;
    }
    const phase = watchMissSequence.sequence?.phase;
    if (phase === 'answer' || phase === 'retry') {
      const delay =
        phase === 'answer'
          ? watchAutoplayMistakeAnswerMs(autoplayStepMs)
          : getMissAnimationDuration(false);
      const id = window.setTimeout(() => {
        clearWatchMistake();
      }, delay);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [
    activeWatchMistake,
    autoplayStepMs,
    clearWatchMistake,
    watchMissSequence.display.animating,
    watchMissSequence.sequence?.phase,
  ]);

  const watchMistakeActive = activeWatchMistake !== null;

  const boardOrientation = userColor
    ? boardOrientationForTrainSide(userColor)
    : 'white';

  const boardPosition = watchMistakeActive
    ? watchMissSequence.display.fen ?? browse.displayFen
    : browse.displayFen;

  const customArrows = useMemo<ChessboardArrow[]>(() => {
    if (watchMistakeActive && watchMissSequence.sequence) {
      return watchMissSequence.display.arrows;
    }
    return [];
  }, [
    watchMistakeActive,
    watchMissSequence.display.arrows,
    watchMissSequence.sequence,
  ]);

  // Red X is drill-only user feedback; watch autoplay uses quality dots + refutation !.
  const incorrectMoveSquare = null;

  const refutationMoveSquare = watchMistakeActive
    ? watchMissSequence.display.refutationMoveSquare
    : null;

  const lastMoveUci = watchMistakeActive
    ? watchMissSequence.display.lastMoveUci
    : browse.lastMoveUci;

  const animationDuration = watchMistakeActive
    ? watchAutoplayMissAnimationDuration(
        watchMissSequence.display.animating,
        autoplayStepMs,
      )
    : REVIEW_MOVE_ANIMATION_MS;

  const moveQualityDisplayDurationMs =
    watchAutoplay && !watchMistakeActive && !pendingWatchMistakePause
      ? Math.max(
          0,
          autoplayStepMs - (animationDuration ?? REVIEW_MOVE_ANIMATION_MS),
        )
      : undefined;
  const activeMoveQualityBadgePly = useMoveQualityBadgePly(
    analyzedMoves,
    browse.plyIndex,
    animationDuration ?? REVIEW_MOVE_ANIMATION_MS,
    {
      displayDurationMs: moveQualityDisplayDurationMs,
      suppressed: shouldSuppressMoveQualityDot(watchMissSequence.sequence?.phase),
    },
  );

  const moveQualitySquareStyles = useMemo(
    () => buildMoveQualitySquareStyles(analyzedMoves, activeMoveQualityBadgePly),
    [activeMoveQualityBadgePly, analyzedMoves],
  );

  return {
    boardOrientation,
    boardPosition,
    customArrows,
    incorrectMoveSquare,
    refutationMoveSquare,
    lastMoveUci,
    animationDuration,
    moveQualitySquareStyles,
  };
}
